import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag
} from "antd";
import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import { Success, Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { createEntry, errorText, updateEntry } from "./api";
import { TAX_KINDS, TAX_KIND_LABELS, sumCredit, sumDebit } from "./labels";
import { filterAccountOption } from "./useAccounts";

// Quick bookings: two lines resolved through account roles, never numbers.
const TEMPLATES = [
  {
    key: "private_withdrawal",
    label: "Private withdrawal (Privatentnahme)",
    debit: "private_withdrawal",
    credit: "bank",
    text: "Privatentnahme"
  },
  {
    key: "private_deposit",
    label: "Private deposit (Privateinlage)",
    debit: "bank",
    credit: "private_deposit",
    text: "Privateinlage"
  },
  {
    key: "bank_to_cash",
    label: "Bank → cash (Bank an Kasse)",
    debit: "cash",
    credit: "bank",
    text: "Bank an Kasse"
  },
  {
    key: "bank_fees",
    label: "Bank fee (Bankgebühr)",
    debit: "bank_fees",
    credit: "bank",
    text: "Bankgebühr"
  }
];

// Booking lines editor: one grid row per line, scrolls horizontally on narrow screens.
const LINE_GRID = {
  display: "grid",
  gridTemplateColumns:
    "minmax(220px, 2.2fr) 150px 150px 150px 80px 150px 170px minmax(160px, 1.5fr) 44px",
  columnGap: 8,
  alignItems: "start",
  minWidth: 1180
};

const LINE_HEAD = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ds-text-2)",
  paddingBlockEnd: 4
};

const emptyLine = () => ({
  account: undefined,
  debit: null,
  credit: null,
  taxKind: "none",
  taxRate: 0,
  taxAmount: null,
  costCentre: null,
  text: ""
});

const toForm = entry => ({
  date: entry?.date ? dayjs(entry.date) : dayjs(),
  belegDate: entry?.belegDate ? dayjs(entry.belegDate) : null,
  belegRef: entry?.belegRef || "",
  text: entry?.text || "",
  lines: entry?.lines?.length
    ? entry.lines.map(l => ({
        account: l.account,
        debit: l.debit || null,
        credit: l.credit || null,
        taxKind: l.taxKind || "none",
        taxRate: l.taxRate || 0,
        taxAmount: l.taxAmount || null,
        costCentre: l.costCentre || null,
        text: l.text || ""
      }))
    : [emptyLine(), emptyLine()]
});

const EntryModal = ({ open, entry, accounts, projects, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [templateAmount, setTemplateAmount] = useState(null);
  const lines = Form.useWatch("lines", form) || [];
  const debit = sumDebit(lines);
  const credit = sumCredit(lines);
  const balanced = debit === credit && debit > 0;

  useEffect(() => {
    if (open) {
      form.setFieldsValue(toForm(entry));
      setTemplate(null);
      setTemplateAmount(null);
    }
  }, [open, entry, form]);

  const applyTemplate = () => {
    const tpl = TEMPLATES.find(x => x.key === template);
    if (!tpl || !templateAmount) return;
    const debitAcc = accounts.byRole.get(tpl.debit);
    const creditAcc = accounts.byRole.get(tpl.credit);
    if (!debitAcc || !creditAcc) {
      ErrorMsg(t("The chart of accounts has no account for this template"));
      return;
    }
    form.setFieldsValue({
      text: form.getFieldValue("text") || tpl.text,
      lines: [
        { ...emptyLine(), account: debitAcc.number, debit: templateAmount },
        { ...emptyLine(), account: creditAcc.number, credit: templateAmount }
      ]
    });
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!balanced) return;
    const body = {
      date: values.date.format("YYYY-MM-DD"),
      belegDate: values.belegDate ? values.belegDate.format("YYYY-MM-DD") : "",
      belegRef: values.belegRef || "",
      text: values.text || "",
      lines: values.lines.map(l => ({
        account: l.account,
        debit: l.debit || 0,
        credit: l.credit || 0,
        taxKind: l.taxKind || "none",
        taxRate: l.taxKind && l.taxKind !== "none" ? l.taxRate || 0 : 0,
        taxAmount: l.taxKind && l.taxKind !== "none" ? l.taxAmount || 0 : 0,
        costCentre: l.costCentre || null,
        text: l.text || ""
      }))
    };
    setSaving(true);
    try {
      if (entry?._id) {
        await updateEntry(entry._id, body);
        Success(t("Entry updated"));
      } else {
        await createEntry(body);
        Success(t("Entry posted"));
      }
      onSaved();
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not post the entry")));
    } finally {
      setSaving(false);
    }
  };

  const projectOptions = (projects || []).map(p => ({
    value: p._id,
    label: p.projectName || p.name || p._id
  }));

  const footer = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8
      }}
    >
      <Space size="middle" wrap style={{ textAlign: "start" }}>
        <span>
          {t("Debit (Soll)")}: <Amount cents={debit} strong />
        </span>
        <span>
          {t("Credit (Haben)")}: <Amount cents={credit} strong />
        </span>
        {balanced ? (
          <Tag color="success">{t("Balanced")}</Tag>
        ) : (
          <Tag color="error">
            {t("Difference")}: <Amount cents={debit - credit} />
          </Tag>
        )}
      </Space>
      <div className="acct-actions" style={{ marginInlineStart: "auto" }}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button type="primary" loading={saving} disabled={!balanced} onClick={submit}>
          {entry?._id ? t("Save") : t("Post entry")}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      rootClassName="acct-module"
      open={open}
      onCancel={onClose}
      width={1100}
      title={
        entry?._id
          ? t("Edit entry {{no}}", { no: `${entry.entryNo}/${entry.year}` })
          : t("New manual booking")
      }
      footer={footer}
      destroyOnClose
    >
      {!entry?._id ? (
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          <Col xs={24} md={12}>
            <Select
              prefix={<ThunderboltOutlined />}
              placeholder={t("Quick booking")}
              style={{ width: "100%" }}
              value={template}
              onChange={setTemplate}
              options={TEMPLATES.map(x => ({ value: x.key, label: t(x.label) }))}
              allowClear
            />
          </Col>
          <Col xs={16} md={7}>
            <MoneyInput value={templateAmount} onChange={setTemplateAmount} placeholder="0,00" />
          </Col>
          <Col xs={8} md={5}>
            <Button block onClick={applyTemplate} disabled={!template || !templateAmount}>
              {t("Apply")}
            </Button>
          </Col>
        </Row>
      ) : null}
      <Form form={form} layout="vertical" initialValues={toForm(entry)}>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} lg={5}>
            <Form.Item
              name="date"
              label={t("Booking date")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Form.Item name="belegDate" label={t("Document date (Belegdatum)")}>
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item name="belegRef" label={t("Document no. (Belegnummer)")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Form.Item
              name="text"
              label={t("Booking text (Buchungstext)")}
              rules={[{ required: true, message: t("Required") }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ margin: "4px 0 12px" }}>{t("Lines")}</Divider>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              <div style={{ overflowX: "auto", marginBottom: 8 }}>
                <div style={LINE_GRID}>
                  <div style={LINE_HEAD}>{t("Account")}</div>
                  <div style={LINE_HEAD}>{t("Debit")}</div>
                  <div style={LINE_HEAD}>{t("Credit")}</div>
                  <div style={LINE_HEAD}>{t("Tax kind")}</div>
                  <div style={LINE_HEAD}>{t("Tax rate")}</div>
                  <div style={LINE_HEAD}>{t("Tax")}</div>
                  <div style={LINE_HEAD}>{t("Project")}</div>
                  <div style={LINE_HEAD}>{t("Text")}</div>
                  <div />
                </div>
                {fields.map(field => {
                  const kind = lines[field.name]?.taxKind;
                  return (
                    <div style={LINE_GRID} key={field.key}>
                      <Form.Item
                        name={[field.name, "account"]}
                        rules={[{ required: true, message: t("Account required") }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          showSearch
                          placeholder={t("Account")}
                          options={accounts.options}
                          filterOption={filterAccountOption}
                          loading={accounts.loading}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, "debit"]} style={{ marginBottom: 8 }}>
                        <MoneyInput placeholder={t("Debit")} min={0} />
                      </Form.Item>
                      <Form.Item name={[field.name, "credit"]} style={{ marginBottom: 8 }}>
                        <MoneyInput placeholder={t("Credit")} min={0} />
                      </Form.Item>
                      <Form.Item name={[field.name, "taxKind"]} style={{ marginBottom: 8 }}>
                        <Select
                          options={TAX_KINDS.map(k => ({ value: k, label: t(TAX_KIND_LABELS[k]) }))}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, "taxRate"]} style={{ marginBottom: 8 }}>
                        <InputNumber
                          min={0}
                          max={19}
                          disabled={!kind || kind === "none"}
                          style={{ width: "100%" }}
                          placeholder="%"
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, "taxAmount"]} style={{ marginBottom: 8 }}>
                        <MoneyInput placeholder={t("Tax")} disabled={!kind || kind === "none"} />
                      </Form.Item>
                      <Form.Item name={[field.name, "costCentre"]} style={{ marginBottom: 8 }}>
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder={t("Project")}
                          options={projectOptions}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, "text"]} style={{ marginBottom: 8 }}>
                        <Input placeholder={t("Text")} />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        aria-label={t("Delete")}
                        disabled={fields.length <= 2}
                        onClick={() => remove(field.name)}
                      />
                    </div>
                  );
                })}
              </div>
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add(emptyLine())} block>
                {t("Add line")}
              </Button>
            </>
          )}
        </Form.List>
      </Form>
      {!balanced ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={t("Debit and credit must be equal before the entry can be posted.")}
        />
      ) : null}
    </Modal>
  );
};

export default EntryModal;
